import type { IRbac } from "./rbac.interfaces";

export enum UserCategory {
  VISITOR = 'visitor',
  GUARDIAN = 'guardian',
  STAFF = 'staff',
  EXTERNAL = 'external',
  STUDENT = 'student',
}

export const CATEGORY_DISPLAY_LABELS: Record<UserCategory, string> = {
  [UserCategory.VISITOR]: 'Visitor',
  [UserCategory.GUARDIAN]: 'Guardian',
  [UserCategory.STAFF]: 'Staff',
  [UserCategory.EXTERNAL]: 'External',
  [UserCategory.STUDENT]: 'Student',
};

/**
 * Full permission structure for RBAC.
 * Used when VITE_APP_WITH_MOCK_DATA=true; from API when false.
 * Guardian uses flat submodules (same IRbac shape).
 */
export const RBAC_PERMISSION_STRUCTURE: IRbac = {
  dashboard: {
    overview: { read: true, write: false, delete: false },
    analytics: { read: true, write: false, delete: false },
  },
  institute: {
    details: { read: true, write: true, delete: false },
    settings: { read: true, write: true, delete: false },
  },
  spocPremise: {
    spocs: { read: true, write: true, delete: true },
    premises: { read: true, write: true, delete: true },
  },
  compliance: {
    documents: { read: true, write: true, delete: true },
    policies: { read: true, write: true, delete: true },
  },
  feeStructure: {
    categories: { read: true, write: true, delete: true },
    schedules: { read: true, write: true, delete: true },
    pricingRules: { read: true, write: true, delete: true },
    gradeFeeStructure: { read: true, write: true, delete: true },
  },
  /** Institute Academics (unified OBEP + academic admin); mirrors matrix `academics` submodules. */
  academics: {
    library: { read: true, write: true, delete: true },
    materials: { read: true, write: true, delete: true },
    schedules: { read: true, write: true, delete: true },
    curriculum: { read: true, write: true, delete: true },
    assessments: { read: true, write: true, delete: true },
    lessonPlans: { read: true, write: true, delete: true },
    obep: { read: true, write: true, delete: true },
    courses: { read: true, write: true, delete: true },
    subjects: { read: true, write: true, delete: true },
    classes: { read: true, write: true, delete: true },
    scoringRubrics: { read: true, write: true, delete: true },
    syllabus: { read: true, write: true, delete: true },
    syllabusAssignment: { read: true, write: true, delete: true },
  },
  assessment: {
    frameworks: { read: true, write: true, delete: true },
    scoringStructure: { read: true, write: true, delete: true },
    gradeCriteria: { read: true, write: true, delete: true },
    evaluationFramework: { read: true, write: true, delete: true },
  },
  branding: {
    assets: { read: true, write: true, delete: true },
    theme: { read: true, write: true, delete: false },
  },
  localization: {
    settings: { read: true, write: true, delete: false },
  },
  rbac: {
    roles: { read: true, write: true, delete: true },
    users: { read: true, write: true, delete: false },
  },
  auditTrail: {
    logs: { read: true, write: false, delete: false },
  },
  help: {
    documentation: { read: true, write: false, delete: false },
  },
  // Guardian: flat submodules (same IRbac shape)
  guardian: {
    profile: { read: true, write: true, delete: false },
    children: { read: true, write: false, delete: false },
    notifications: { read: true, write: true, delete: true },
  },
};
